import React from 'react';
import { Link } from 'react-router-dom';
import {
  Plug,
  Mail,
  FileText,
  CreditCard,
  Brain,
  MapPin,
  FileCheck,
  ArrowRight,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { Badge } from '../../../../components/ui/badge';
import { cn } from '../../../../lib/utils';

const INTEGRATIONS = [
  {
    id: 'mailgun',
    name: 'Mailgun',
    description: 'Transactional email delivery service',
    icon: Mail,
    path: '/platform/settings/integrations/mailgun',
    category: 'Communication',
    status: 'configured' // This would come from backend in production
  },
  {
    id: 'postgrid',
    name: 'PostGrid',
    description: 'Direct mail and address verification',
    icon: FileText,
    path: '/platform/settings/integrations/postGrid',
    category: 'Communication',
    status: 'not_configured'
  },
  {
    id: 'stripe',
    name: 'Stripe',
    description: 'Payment processing and billing',
    icon: CreditCard,
    path: '/platform/settings/integrations/stripe',
    category: 'Payments',
    status: 'configured'
  },
  {
    id: 'openai',
    name: 'OpenAI',
    description: 'AI-powered features and analysis',
    icon: Brain,
    path: '/platform/settings/integrations/openai',
    category: 'AI & ML',
    status: 'configured'
  },
  {
    id: 'google-address',
    name: 'Google Address Services',
    description: 'Address autocomplete and validation',
    icon: MapPin,
    path: '/platform/settings/integrations/google-address',
    category: 'Utilities',
    status: 'configured'
  },
  {
    id: 'credit-providers',
    name: 'Credit Report Providers',
    description: 'IdentityIQ and other credit data sources',
    icon: FileCheck,
    path: '/platform/settings/integrations/credit-providers',
    category: 'Data',
    status: 'configured'
  },
];

const CATEGORIES = ['Communication', 'Payments', 'AI & ML', 'Utilities', 'Data'];

const PlatformIntegrationsHubPage = () => {
  const getStatusBadge = (status) => {
    switch (status) {
      case 'configured':
        return (
          <Badge className="bg-emerald-500/20 text-emerald-400 border-0">
            <CheckCircle className="w-3 h-3 mr-1" />
            Configured
          </Badge>
        );
      case 'error':
        return (
          <Badge className="bg-red-500/20 text-red-400 border-0">
            <XCircle className="w-3 h-3 mr-1" />
            Error
          </Badge>
        );
      default:
        return (
          <Badge className="bg-zinc-600/50 text-zinc-400 border-0">
            <AlertCircle className="w-3 h-3 mr-1" />
            Not Configured
          </Badge>
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1F18] p-8" data-testid="platform-integrations-hub-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center">
          <Plug className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Integrations Hub</h1>
          <p className="text-[#93AE9F] text-sm">
            Manage platform-level API connections and service integrations
          </p>
        </div>
      </div>

      {/* Categories */}
      {CATEGORIES.map((category) => {
        const categoryIntegrations = INTEGRATIONS.filter(i => i.category === category);
        if (categoryIntegrations.length === 0) return null;
        
        return (
          <div key={category} className="mb-8">
            <h2 className="text-sm font-semibold text-[#93AE9F] uppercase tracking-wider mb-4">
              {category}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categoryIntegrations.map((integration) => {
                const Icon = integration.icon;
                return (
                  <Link
                    key={integration.id}
                    to={integration.path}
                    className="group bg-[rgba(10,38,28,0.6)] border border-[rgba(142,218,182,0.12)] rounded-xl p-6 hover:border-emerald-500/30 transition-all"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                        <Icon className="w-5 h-5 text-emerald-400" />
                      </div>
                      {getStatusBadge(integration.status)}
                    </div>
                    
                    <h3 className="font-semibold text-white mb-1 group-hover:text-emerald-400 transition-colors">
                      {integration.name}
                    </h3>
                    <p className="text-sm text-[#93AE9F] mb-4">
                      {integration.description}
                    </p>
                    
                    <div className="flex items-center text-sm text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity">
                      Configure
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Info Box */}
      <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
        <div className="flex items-start gap-3">
          <Plug className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-400 mb-1">About Integrations</h4>
            <p className="text-sm text-blue-300/80">
              This hub provides quick access to all platform integrations. Each integration 
              has its own dedicated settings page where you can configure API keys, test 
              connections, and manage provider-specific options. Changes here affect the 
              entire platform.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformIntegrationsHubPage;
